// Main Application Router with Portal-Based Organization
import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../shared/hooks';
import { LoadingSpinner, ErrorBoundary } from '../shared/components';

// Route Guards
import {
  PrivateRoute,
  PermissionRoute,
  BureauPortalRoute,
  AdminPortalRoute,
  ClientPortalRoute,
  SuperAdminPortalRoute,
  TiplockPortalRoute,
  SalesPortalRoute,
  TechnicianPortalRoute,
  InsurancePortalRoute,
  PortalLayoutWrapper,
} from './guards';

// Route Configurations
import {
  commonRoutes,
  bureauRoutes,
  adminRoutes,
  clientRoutes,
  superAdminRoutes,
  supportRoutes,
  tiplockRoutes,
  salesRoutes,
  technicianRoutes,
  insuranceRoutes,
  getDefaultRedirectForUser,
} from './routes';

// Layout Components (lazy loaded)
import { LayoutProvider } from '../shared/layout/LayoutProvider';

// Lazy loaded components
const MainLayout = React.lazy(() => import('../shared/layout/BaseLayout'));
const AuthLayout = React.lazy(() => import('../shared/layout/AuthLayout'));
const BureauLayout = React.lazy(() => import('../portals/bureau/layouts/BureauLayout'));
const AdminLayout = React.lazy(() => import('../portals/admin/layouts/AdminLayout'));
const ClientPortalLayout = React.lazy(() => import('../portals/client/layouts/ClientPortalLayout'));
const SuperAdminPortalLayout = React.lazy(
  () => import('../portals/super-admin/layouts/SuperAdminPortalLayout')
);
const SalesPortalLayout = React.lazy(() => import('../portals/sales/layouts/SalesPortalLayout'));
const SupportPortalLayout = React.lazy(
  () => import('../portals/support/layouts/SupportPortalLayout')
);
const TechnicianLayout = React.lazy(() => import('../portals/technician/layouts/TechnicianLayout'));
const InsurancePortalLayout = React.lazy(
  () => import('../portals/insurance/layouts/InsurancePortalLayout')
);

// Demo Portal (lazy loaded — standalone route, no sales layout)
const DemoClientPortal = React.lazy(() => import('../portals/sales/demo/DemoClientPortal'));

// Login components (lazy loaded)
const UnifiedLogin = React.lazy(() => import('../shared/components/auth/UnifiedLogin'));
const SuperAdminLogin = React.lazy(() => import('../shared/components/auth/SuperAdminLogin'));

/**
 * Render route content with appropriate guards and layout
 * Returns the component content, not a Route wrapper
 */
const renderRouteContent = (route, LayoutComponent = null) => {
  // Determine if route needs authentication
  const needsAuth = !route.isPublic;

  // Render route content
  let routeContent = <route.component />;

  // Wrap with permission guard if needed
  if (route.requiredPermissions && route.requiredPermissions.length > 0) {
    routeContent = (
      <PermissionRoute requiredPermissions={route.requiredPermissions}>
        {routeContent}
      </PermissionRoute>
    );
  }

  // Wrap with layout if provided
  if (LayoutComponent) {
    routeContent = <LayoutComponent>{routeContent}</LayoutComponent>;
  }

  // Wrap with private route if authentication required
  if (needsAuth) {
    routeContent = <PrivateRoute>{routeContent}</PrivateRoute>;
  }

  return routeContent;
};

/**
 * Render a route with appropriate guards and layout
 * Returns a Route component for use in Routes
 */
const renderRoute = (route, parentPath = '', LayoutComponent = null) => {
  const fullPath = parentPath + (route.path || '');
  const key = route.index ? `${parentPath}-index` : fullPath;

  const routeContent = renderRouteContent(route, LayoutComponent);

  return (
    <Route
      key={key}
      path={route.index ? undefined : fullPath}
      index={route.index}
      element={routeContent}
    />
  );
};

/**
 * Render nested routes recursively
 */
const renderNestedRoutes = (routes, parentPath = '', LayoutComponent = null) => {
  return routes.map((route) => {
    // If route has children, create a Route with nested Routes
    if (route.children && route.children.length > 0) {
      const routeContent = renderRouteContent(route, LayoutComponent);

      return (
        <Route key={route.path || 'index'} path={route.path || ''} element={routeContent}>
          {renderNestedRoutes(route.children, '', LayoutComponent)}
        </Route>
      );
    }

    // Simple route without children
    const routeElement = renderRoute(route, parentPath, LayoutComponent);
    return routeElement;
  });
};

/**
 * Portal Routes Component - Renders all portal routes with appropriate guards
 */
const PortalRoutes = () => {
  return (
    <Routes>
      {/* Bureau Portal Routes - No Layout Wrapper */}
      <Route
        path="/bureau/*"
        element={
          <BureauPortalRoute>
            <Suspense
              fallback={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    width: '100vw',
                  }}
                >
                  <LoadingSpinner size="large" message="Loading Bureau Portal..." />
                </div>
              }
            >
              <Routes>
                {renderNestedRoutes(bureauRoutes.children, '')}
                {/* An unmatched path inside a portal used to render NOTHING:
                   no error, no redirect, just a white page. /bureau/dashboard did
                   this, because the dashboard is the index route at /bureau. */}
                <Route path="*" element={<Navigate to="/bureau" replace />} />
              </Routes>
            </Suspense>
          </BureauPortalRoute>
        }
      />

      {/* Admin Portal Routes */}
      <Route
        path="/admin/*"
        element={
          <AdminPortalRoute>
            <PortalLayoutWrapper portal="admin">
              <Suspense
                fallback={
                  <div className="layout-loading">
                    <LoadingSpinner size="large" message="Loading Admin Portal..." />
                  </div>
                }
              >
                <AdminLayout>
                  <Routes>
                    {renderNestedRoutes(adminRoutes.children, '')}
                    {/* An unmatched path inside a portal used to render NOTHING:
                   no error, no redirect, just a white page. /bureau/dashboard did
                   this, because the dashboard is the index route at /bureau. */}
                    <Route path="*" element={<Navigate to="/admin" replace />} />
                  </Routes>
                </AdminLayout>
              </Suspense>
            </PortalLayoutWrapper>
          </AdminPortalRoute>
        }
      />

      {/* Client Portal Routes with Layout */}
      <Route
        path="/client/*"
        element={
          <ClientPortalRoute>
            <Suspense
              fallback={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    width: '100vw',
                  }}
                >
                  <LoadingSpinner size="large" message="Loading Client Portal..." />
                </div>
              }
            >
              <ClientPortalLayout>
                <Routes>
                  {renderNestedRoutes(clientRoutes.children, '')}
                  {/* An unmatched path inside a portal used to render NOTHING:
                   no error, no redirect, just a white page. /bureau/dashboard did
                   this, because the dashboard is the index route at /bureau. */}
                  <Route path="*" element={<Navigate to="/client" replace />} />
                </Routes>
              </ClientPortalLayout>
            </Suspense>
          </ClientPortalRoute>
        }
      />

      {/* Super Admin Portal Routes with Layout */}
      <Route
        path="/super-admin/*"
        element={
          <SuperAdminPortalRoute>
            <Suspense
              fallback={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    width: '100vw',
                  }}
                >
                  <LoadingSpinner size="large" message="Loading Super Admin Portal..." />
                </div>
              }
            >
              <SuperAdminPortalLayout>
                <Routes>
                  {renderNestedRoutes(superAdminRoutes.children, '')}
                  {/* An unmatched path inside a portal used to render NOTHING:
                   no error, no redirect, just a white page. /bureau/dashboard did
                   this, because the dashboard is the index route at /bureau. */}
                  <Route path="*" element={<Navigate to="/super-admin" replace />} />
                </Routes>
              </SuperAdminPortalLayout>
            </Suspense>
          </SuperAdminPortalRoute>
        }
      />

      {/* Support Portal Routes - No Layout Wrapper (handles its own layout like Bureau) */}
      <Route
        path="/support/*"
        element={
          <PrivateRoute>
            <Suspense
              fallback={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    width: '100vw',
                  }}
                >
                  <LoadingSpinner size="large" message="Loading Support Portal..." />
                </div>
              }
            >
              <SupportPortalLayout>
                <Routes>
                  {renderNestedRoutes(supportRoutes.children, '')}
                  {/* An unmatched path inside a portal used to render NOTHING:
                   no error, no redirect, just a white page. /bureau/dashboard did
                   this, because the dashboard is the index route at /bureau. */}
                  <Route path="*" element={<Navigate to="/support" replace />} />
                </Routes>
              </SupportPortalLayout>
            </Suspense>
          </PrivateRoute>
        }
      />

      {/* TipLock Portal Routes - No Layout Wrapper (handles its own layout like Bureau) */}
      <Route
        path="/tiplock/*"
        element={
          <TiplockPortalRoute>
            <Suspense
              fallback={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    width: '100vw',
                  }}
                >
                  <LoadingSpinner size="large" message="Loading TipLock Portal..." />
                </div>
              }
            >
              <Routes>
                {renderNestedRoutes(tiplockRoutes.children, '')}
                {/* An unmatched path inside a portal used to render NOTHING:
                   no error, no redirect, just a white page. /bureau/dashboard did
                   this, because the dashboard is the index route at /bureau. */}
                <Route path="*" element={<Navigate to="/tiplock" replace />} />
              </Routes>
            </Suspense>
          </TiplockPortalRoute>
        }
      />

      {/* Technician Portal Routes — mobile-first, gated by assigned_portal='technician' */}
      <Route
        path="/technician/*"
        element={
          <TechnicianPortalRoute>
            <PortalLayoutWrapper portal="technician">
              <Suspense
                fallback={
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100vh',
                      width: '100vw',
                    }}
                  >
                    <LoadingSpinner size="large" message="Loading Technician Portal..." />
                  </div>
                }
              >
                <TechnicianLayout>
                  <Routes>
                    {renderNestedRoutes(technicianRoutes.children, '')}
                    {/* An unmatched path inside a portal used to render NOTHING:
                   no error, no redirect, just a white page. /bureau/dashboard did
                   this, because the dashboard is the index route at /bureau. */}
                    <Route path="*" element={<Navigate to="/technician" replace />} />
                  </Routes>
                </TechnicianLayout>
              </Suspense>
            </PortalLayoutWrapper>
          </TechnicianPortalRoute>
        }
      />

      {/* Insurance Portal Routes — admin-only scaffold, gated by canAccessPortal('insurance') */}
      <Route
        path="/insurance/*"
        element={
          <InsurancePortalRoute>
            <PortalLayoutWrapper portal="insurance">
              <Suspense
                fallback={
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100vh',
                      width: '100vw',
                    }}
                  >
                    <LoadingSpinner size="large" message="Loading Insurance Portal..." />
                  </div>
                }
              >
                <InsurancePortalLayout>
                  <Routes>
                    {renderNestedRoutes(insuranceRoutes.children, '')}
                    <Route path="*" element={<Navigate to="/insurance" replace />} />
                  </Routes>
                </InsurancePortalLayout>
              </Suspense>
            </PortalLayoutWrapper>
          </InsurancePortalRoute>
        }
      />

      {/* Demo Portal Routes — standalone layout, requires sales auth */}
      <Route
        path="/demo-portal/:sessionId/*"
        element={
          <SalesPortalRoute>
            <Suspense
              fallback={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    width: '100vw',
                  }}
                >
                  <LoadingSpinner size="large" message="Loading Demo Portal..." />
                </div>
              }
            >
              <DemoClientPortal />
            </Suspense>
          </SalesPortalRoute>
        }
      />

      {/* Sales Portal Routes */}
      <Route
        path="/sales/*"
        element={
          <SalesPortalRoute>
            <Suspense
              fallback={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    width: '100vw',
                  }}
                >
                  <LoadingSpinner size="large" message="Loading Sales Portal..." />
                </div>
              }
            >
              <SalesPortalLayout>
                <Routes>
                  {renderNestedRoutes(salesRoutes.children, '')}
                  {/* An unmatched path inside a portal used to render NOTHING:
                   no error, no redirect, just a white page. /bureau/dashboard did
                   this, because the dashboard is the index route at /bureau. */}
                  <Route path="*" element={<Navigate to="/sales" replace />} />
                </Routes>
              </SalesPortalLayout>
            </Suspense>
          </SalesPortalRoute>
        }
      />
    </Routes>
  );
};

/**
 * Smart Home Route - Redirects authenticated users to appropriate portal
 */
const SmartHomeRoute = () => {
  const { user, isAuthenticated, isLoading, roles } = useAuth();

  if (isLoading) {
    return (
      <div className="route-loading-container">
        <LoadingSpinner size="large" message="Loading..." />
      </div>
    );
  }

  // If not authenticated, show the home page
  if (!isAuthenticated || !user) {
    const HomePage = commonRoutes.find((r) => r.path === '/')?.component;
    return HomePage ? <HomePage /> : <Navigate to="/login" replace />;
  }

  // Enhanced user object with roles from Redux state
  const userWithRoles = {
    ...user,
    roles: roles || [], // Always use roles from auth state
    groups: user?.groups || [],
  };

  const defaultRedirect = getDefaultRedirectForUser(userWithRoles);

  // Redirect authenticated users to their portal
  return <Navigate to={defaultRedirect} replace />;
};

/**
 * Main Application Router Component
 */
export const AppRouter = () => {
  return (
    <LayoutProvider>
      <ErrorBoundary>
        <Suspense
          fallback={
            <div className="app-loading">
              <LoadingSpinner size="large" message="Loading FleetCam..." />
            </div>
          }
        >
          <Routes>
            {/* Smart Home Route - Only for exact root path */}
            <Route path="/" element={<SmartHomeRoute />} />

            {/* Authentication Routes */}
            {/* Unified login for all users except Super Admin */}
            <Route
              path="/login"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <UnifiedLogin />
                </Suspense>
              }
            />

            {/* Separate Super Admin login for high security */}
            <Route
              path="/super-admin/login"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <SuperAdminLogin />
                </Suspense>
              }
            />

            <Route
              path="/reset-password/:uidb64/:token"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <AuthLayout>
                    {renderRouteContent(
                      commonRoutes.find((r) => r.path === '/reset-password/:uidb64/:token')
                    )}
                  </AuthLayout>
                </Suspense>
              }
            />

            {/* Public Client Intake Form (token-based, no auth) */}
            <Route
              path="/client-intake/:token"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  {renderRouteContent(commonRoutes.find((r) => r.path === '/client-intake/:token'))}
                </Suspense>
              }
            />

            {/* Portal Routes */}
            <Route path="/*" element={<PortalRoutes />} />

            {/* Common Protected Routes */}
            <Route
              path="/profile"
              element={
                <PrivateRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <MainLayout>
                      {renderRouteContent(commonRoutes.find((r) => r.path === '/profile'))}
                    </MainLayout>
                  </Suspense>
                </PrivateRoute>
              }
            />

            {/* Public Routes */}
            <Route
              path="/help"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <MainLayout>
                    {renderRouteContent(commonRoutes.find((r) => r.path === '/help'))}
                  </MainLayout>
                </Suspense>
              }
            />

            {/* Error Routes */}
            <Route
              path="/unauthorized"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <MainLayout>
                    {renderRouteContent(commonRoutes.find((r) => r.path === '/unauthorized'))}
                  </MainLayout>
                </Suspense>
              }
            />

            <Route
              path="/404"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <MainLayout>
                    {renderRouteContent(commonRoutes.find((r) => r.path === '/404'))}
                  </MainLayout>
                </Suspense>
              }
            />

            {/* Catch All - 404 */}
            <Route
              path="*"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <MainLayout>
                    {renderRouteContent(commonRoutes.find((r) => r.path === '*'))}
                  </MainLayout>
                </Suspense>
              }
            />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </LayoutProvider>
  );
};

export default AppRouter;
